/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Input, InputGroup, InputGroupText, Label } from 'reactstrap';
import { ROUTES } from '../../../constants/routes';
import { generateUrl } from '../../../routes/util';
import { reloadPortfolioCompanies } from '../../../slice/portfolioCompanySlice';
import { DataScopeAccess, DataShareScopeView, RequesterType } from '../../../types/dataShare';
import { InitiativeCompany, InitiativeType } from '../../../types/initiative';
import { Portfolio } from '../../../types/portfolio';
import { naturalSort } from '../../../utils';
import { BasicAlert } from '../../alert/BasicAlert';
import { DataShareModal, getDefaultContent } from '../../data-share/DataShareModal';
import SimpleTooltip from '../../simple-tooltip';
import Table, { TableColumn, TableVariant } from '../../table/Table';
import { MenuButton } from '../menu-button';
import AccessStatus from './partials/access-status';
import { Column, ColumnType, GeneralColumns, WeightedInitiativeCompany, getAnsweredRatio, getGeneralColumnsFn, getLastCompletedSurvey } from './utils';
import { RequesterMessageModal } from '../../message-modal/RequesterMessageModal';
import { useAppSelector } from '../../../reducers';
import { isUserManagerByInitiativeId } from '../../../selectors/user';
import { getDefaultRequesterDataScope, getViewAccess, isViewShared } from '../../../utils/dataShare';
import { updateHoldingWeightFromPortfolio } from '@actions/portfolio-tracker';
import { useSiteAlert } from '@hooks/useSiteAlert';

interface ExchangeTableProps {
  portfolio: Portfolio;
  companies: WeightedInitiativeCompany[];
  hasFilters: boolean;
  columnsSetting: Column[];
  handleReload: () => void;
  allMetrics: boolean;
  toggleViewAllMetrics: () => void;
}

const ExchangeTable = ({
  allMetrics,
  portfolio,
  companies,
  hasFilters,
  columnsSetting,
  handleReload,
  toggleViewAllMetrics,
}: ExchangeTableProps) => {
  const dispatch = useDispatch();
  const isUserManager = useAppSelector((state) => isUserManagerByInitiativeId(state, portfolio._id));
  const [shareModal, setShareModal] = useState<{ company?: Pick<InitiativeCompany, '_id' | 'name'> }>({});
  const [messageModal, setMessageModal] = useState<{ company?: Pick<InitiativeCompany, '_id' | 'name'> }>({});
  const toggleMessageModal = () => setMessageModal({});
  const [isSaving, setSaving] = useState(false);
  const { addSiteError } = useSiteAlert();

  const handleUpdateWeight = async (holdingId: string | undefined, originalWeight: number, weight: number) => {
    if (!holdingId || originalWeight === weight || isSaving) {
      return;
    }
    setSaving(true);
    try {
      await updateHoldingWeightFromPortfolio(portfolio._id, holdingId, weight);
      handleReload();
    } catch (e) {
      addSiteError(e);
    }
    setSaving(false);
  };
  
  const history = useHistory();
  const handleClickCompany = (initiativeId: string) => {
    history.push(
      generateUrl(ROUTES.PORTFOLIO_TRACKER_COMPANY, { portfolioId: portfolio._id, companyId: initiativeId })
    );
  };
  const tableColumns: TableColumn<InitiativeCompany>[] = [
    {
      id: 'menu-button',
      accessor: '_id',
      Header: () => '',
      cellProps: {
        className: 'text-left',
      },
      Cell: (c) => {
        if ('cashLine' in c.row.original || !isUserManager) {
          return null;
        }
        const company = c.row.original;
        const isPortfolio = company.type === InitiativeType.initiativeGroup;
        return (
          <MenuButton
            disabled={!isUserManager}
            onShare={(c: Pick<InitiativeCompany, '_id' | 'name'>) => setShareModal((s) => ({ ...s, company: c }))}
            isPortfolio={isPortfolio}
            portfolioId={portfolio._id}
            holdingId={c.value}
            company={company}
            setSaving={() => {}}
            handleReload={handleReload}
            dropdownProps={{ direction: 'end' }}
            handleSendMessage={(c: Pick<InitiativeCompany, '_id' | 'name'>) =>
              setMessageModal((s) => ({ ...s, company: c }))
            }
          />
        );
      },
      disableSortBy: true,
      sticky: 'left',
    },
    {
      accessor: '_id',
      Header: () => 'Access',
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => {
        const company = c.row.original;
        return <AccessStatus company={company} />;
      },
      disableSortBy: true,
      sticky: 'left',
    },
    {
      accessor: 'name',
      Header: () => 'Company name',
      Cell: (c) => {
        const company = c.row.original;
        if (getViewAccess(company) === DataScopeAccess.None) {
          return <span className='px-1 text-ThemeTextDark'>{company.name}</span>
        }

        return (
          <Button
            color='link'
            className='text-left'
            onClick={() => handleClickCompany(company._id)}
          >
            {company.name}
          </Button>
        );
      },
      sortType: (a, b) => naturalSort(a.original.name, b.original.name),
      sticky: 'left',
    },
  ];

  columnsSetting.forEach((col: Column) => {
    if (col.type === ColumnType.General) {
      if (col.code === GeneralColumns.Weight) {
        tableColumns.push({
          accessor: col.code,
          Header: col.name,
          cellProps: {
            className: 'weightCol order-2 row-break-1 row-lg-break-0 ml-5 ml-lg-0',
          },
          Cell: (c) => (
            <InputGroup>
              <Input
                type={'number'}
                min={0}
                className='text-center'
                disabled={isSaving}
                defaultValue={Math.round(c.value * 10) / 10}
                onBlur={(e) => handleUpdateWeight(c.row.original._id, Number(c.value), Number(e.target.value))
                }
              />
              <InputGroupText>%</InputGroupText>
            </InputGroup>
          ),
        });
        return;
      }
      tableColumns.push({
        accessor: col.code,
        Header: () => col.name,
        cellProps: {
          className: 'text-center',
        },
        Cell: (c) => {
          const company = c.row.original;
          return isViewShared(company, DataShareScopeView.Survey)
            ? getGeneralColumnsFn(col.code)(c.row.original).display
            : '';
        },
        sortType: (a, b) =>
          naturalSort(getGeneralColumnsFn(col.code)(a.original).sort, getGeneralColumnsFn(col.code)(b.original).sort),
      });
    }

    if (col.type === ColumnType.SurveyPack && !allMetrics) {
      tableColumns.push({
        accessor: col.code,
        Header: () => {
          const hintText = (
            <span>
              Figures from <br /> latest completed <br /> survey pack
            </span>
          );
          return <SimpleTooltip text={hintText}>{col.name}</SimpleTooltip>;
        },
        cellProps: {
          className: 'text-center',
        },
        Cell: (c) => {
          const company = c.row.original;
          if (!isViewShared(company, DataShareScopeView.Survey)) {
            return '';
          }

          const lastCompletedSurvey = getLastCompletedSurvey(company);
          const data = lastCompletedSurvey?.[col.code];
          if (!data) {
            return '-';
          }

          const { private: privateQuestions, na, nr, answered, total } = data;
          const hintText = (
            <div className='p-2'>
              <p className='mb-1'>{privateQuestions} marked Private</p>
              <p className='mb-1'>{na} marked N/A</p>
              <p className='mb-1'>{nr} marked N/R</p>
              <p className='mb-0'>{total - answered} unanswered</p>
            </div>
          );
          return (
            <div>
              {answered}/{total}
              <SimpleTooltip text={hintText}>
                <i className='fa-light fa-circle-exclamation ml-2'></i>
              </SimpleTooltip>
            </div>
          );
        },
        sortType: (first, second) => {
          const firstLastCompletedSurvey = getLastCompletedSurvey(first.original);
          const secondLastCompletedSurvey = getLastCompletedSurvey(second.original);
          const fistAnswered = firstLastCompletedSurvey ? firstLastCompletedSurvey[col.code]?.answered : 0;
          const secondAnswered = secondLastCompletedSurvey ? secondLastCompletedSurvey[col.code]?.answered : 0;
          return naturalSort(fistAnswered, secondAnswered);
        },
      });
    }
  });

  if (allMetrics) {
    tableColumns.push({
      accessor: 'metrics',
      Header: () => 'All Metrics',
      cellProps: {
        className: 'text-center',
      },
      Cell: (c) => getAnsweredRatio(c.row.original).display,
      sortType: (a, b) => naturalSort(getAnsweredRatio(a.original).sort, getAnsweredRatio(b.original).sort),
    });
  }

  const toggleShareModal = (update?: { reload?: boolean }) => {
    setShareModal({});
    if (update?.reload) {
      return dispatch(reloadPortfolioCompanies(portfolio._id));
    }
  };

  return (
    <>
      <div className='exchange-table-container mt-3'>
        <div className='text-right mr-2'>
          <Label check>
            <Input type='checkbox' checked={allMetrics} onChange={toggleViewAllMetrics} className='mr-2' />
            All Metrics
          </Label>
        </div>
        <div className='table-wrapper mt-2'>
          <Table
            columns={tableColumns}
            data={companies}
            pageSize={20}
            className={columnsSetting.length > 0 ? 'exchange-table-fixed' : 'exchange-table-normal'}
            variant={columnsSetting.length > 0 ? TableVariant.StickyColumns : TableVariant.Normal}
          />
          {companies?.length === 0 ? (
            <BasicAlert type='secondary' className='mt-2'>
              {hasFilters ? 'No company match your criteria' : 'You have no companies assigned to this portfolio'}
            </BasicAlert>
          ) : null}
        </div>
      </div>
      {shareModal.company && (
        <DataShareModal
          key={shareModal.company._id}
          isOpen={Boolean(shareModal.company)}
          toggle={toggleShareModal}
          requesterId={portfolio._id}
          requesterType={RequesterType.Portfolio}
          title={`${portfolio.name} is requesting access to ${shareModal.company.name}`}
          content={getDefaultContent({ requesterName: portfolio.name })}
          initiativeId={shareModal.company._id}
          initiativeName={shareModal.company.name}
          dataScope={getDefaultRequesterDataScope()}
        />
      )}
      {messageModal.company ? (
        <RequesterMessageModal
          title={`Message ${messageModal.company.name}`}
          requesterId={portfolio._id}
          initiativeId={messageModal.company._id}
          toggle={toggleMessageModal}
        />
      ) : null}
    </>
  );
};

export default ExchangeTable;
